import { Form } from '@ant-design/compatible';
import { Col } from 'antd';
import Contacts from 'common/contacts/components/contacts';
import ErrorHandler from 'common/error/components/error-handler';
import LockMessage from 'common/lock/components/lock-message';
import ActionButtons from 'common/navigation/components/action-buttons';
import PublishSteps from 'common/publish-steps/containers/publish-steps-container';
import Spinner from 'common/spinner/components/spinner';
import PropTypes from 'prop-types';
import qs from 'query-string';
import React, { Component } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { compose } from 'redux';
import styled from 'styled-components';
import TicketButton from 'tickets/ticket-button';
import {
  DropdownButton,
  Flex,
  Info,
  LastValidated,
  notification,
  Row,
  SubTitle,
  Title,
  TitleActions,
} from 'ui';
import { withRouter } from 'utils/withRouter';
import PermissionCheck from '../../../common/features/permission-check';
import GeoMap from '../../../common/map/components/geo-map';
import { isObjectNullOrEmpty } from '../../../utils/helpers';
import Prompt from '../../../utils/prompt';
import Address from './address';
import ContactInfo from './contact-info';

const NotEditMessage = styled.p`
  margin-left: 15px;
`;

class InstalledAtInfo extends Component {
  static propTypes = {
    error: PropTypes.object,
    fetchInfo: PropTypes.func.isRequired,
    saveInstalledAtInfoRequest: PropTypes.func.isRequired,
    id: PropTypes.string.isRequired,
    history: PropTypes.object.isRequired,
    renderMenu: PropTypes.func.isRequired,
    data: PropTypes.object,
    form: PropTypes.object,
    isSaving: PropTypes.bool,
    saveSuccess: PropTypes.bool,
    isLoading: PropTypes.bool,
    intl: PropTypes.object,
    lock: PropTypes.object,
    lockingErrorType: PropTypes.string,
    verifyInstalledAtInfoRequest: PropTypes.func,
    installedAtInfoSuccess: PropTypes.func,
    match: PropTypes.object,
  };

  constructor(props) {
    super(props);
    this.state = {
      isEditable: qs.parse(props.history.location.search).edit === 'true',
    };
  }

  componentDidMount() {
    if (this.props.id) {
      this.props.fetchInfo({ installedAtId: this.props.id });
    }
  }

  componentWillReceiveProps(nextProps) {
    // TODO: check if this is not an anti-pattern
    if (this.props.saveSuccess !== nextProps.saveSuccess && !nextProps.isSaving) {
      notification.success({
        message: nextProps.intl.formatMessage({ id: 'type.installedat.SAVE_SUCCESS' }),
        placement: 'top',
      });
      this.setState({ isEditable: false });
    }
    if (
      nextProps.id !== this.props.id ||
      nextProps.match.params.timestamp !== this.props.match.params.timestamp
    ) {
      this.props.fetchInfo({ installedAtId: nextProps.id });
    }
    if (!this.state.isEditable && nextProps.lockingErrorType === 'stale') {
      this.setState({ isEditable: true });
    }
  }

  save = () => {
    const { saveInstalledAtInfoRequest, form, data } = this.props;
    const dirty = form.isFieldsTouched();
    form.validateFields((err, values) => {
      if (!err) {
        if (dirty) {
          saveInstalledAtInfoRequest({ data, values });
        } else {
          this.resetForm();
        }
      }
    });
  };

  toggleEditState = () => {
    this.setState({ isEditable: !this.state.isEditable });
  };

  resetForm = () => {
    const { form } = this.props;
    form.resetFields();
    this.toggleEditState();
  };

  verifyInstalledAtInfo = () => {
    const {
      verifyInstalledAtInfoRequest,
      data: { id },
    } = this.props;
    verifyInstalledAtInfoRequest({ id });
  };


  renderButton = () => {
    const { data, intl, lock, isSaving } = this.props;
    const { isEditable } = this.state;

    const dropdownItemsEdit = [
      {
        text: intl.formatMessage({ id: 'type.installedat.CONFIRM_VALIDITY_INFO' }),
        onClick: this.verifyInstalledAtInfo,
      },
    ];

    const dropdownItemsSave = [
      {
        text: intl.formatMessage({ id: 'common.CANCEL' }),
        onClick: this.resetForm,
      },
    ];

    if (isEditable) {
      return <DropdownButton
        disabled={!!lock}
        text={intl.formatMessage({ id: 'common.SAVE' })}
        iconClass={isSaving ? 'fa-spinner fa-spin' : 'fa-save'}
        onClick={this.save}
        subItems={dropdownItemsSave}
      />;
    }
    return <DropdownButton
      disabled={!!lock || isSaving || !data.isEditable}
      text={intl.formatMessage({ id: 'common.EDIT' })}
      iconClass={
        // eslint-disable-next-line
        isSaving ? 'fa-spinner fa-spin' : lock ? 'fa-lock' : 'fa-pencil'
      }
      onClick={this.toggleEditState}
      subItems={dropdownItemsEdit}
    />;
  };

  render() {
    const {
      data,
      error,
      renderMenu,
      form,
      isSaving,
      isLoading,
      intl,
      lock,
      id,
      fetchInfo,
      installedAtInfoSuccess,
    } = this.props;
    const { isEditable } = this.state;
    const disabled = isSaving || isLoading;
    const dirty = form.isFieldsTouched();

    return (
      <>
        {renderMenu(
          <ActionButtons>
            <TicketButton id={id} />
          </ActionButtons>,
        )}
        <ErrorHandler type='notification' error={error} />
        <PublishSteps
          id={id}
          reloadData={() => fetchInfo({ installedAtId: id })}
          onDataReloaded={installedAtInfoSuccess}
          type='installedat'
        />
        {isLoading ?
          <Spinner title={intl.formatMessage({ id: 'common.LOADING_INFO' })} />
          :
          <>
            <Info>
              <Form layout='vertical'>
                <Row type='flex' gutter={16}>
                  <Col span={24}>
                    <Flex>
                      <Title>{data.name}</Title>
                      {isObjectNullOrEmpty(error) ?
                        <PermissionCheck permissionsList={['installedbasemanage']}>
                          <TitleActions>
                            <LockMessage lock={lock} />
                            {this.renderButton()}
                            {!data?.isEditable ?
                              <NotEditMessage>
                                {intl.formatMessage({ id: 'type.installedat.NOT_EDITABLE_INFO' })}
                              </NotEditMessage> :
                              null
                            }
                          </TitleActions>
                        </PermissionCheck> :
                        null}
                    </Flex>
                  </Col>
                </Row>
                <Row type='flex' gutter={25} marginBottom={45}>
                  <Col sm={12} xs={24} data-test-id='address'>
                    <SubTitle>
                      <FormattedMessage id='common.titles.ADDRESS' />
                      <LastValidated date={data.lastValidated} />
                    </SubTitle>
                    <Address data={data} isEditable={isEditable} form={form} disabled={disabled} />
                  </Col>
                  <Col sm={12} xs={24} data-test-id='geo-map'>
                    <GeoMap
                      location={data?.location}
                    />
                  </Col>
                  <Col sm={12} xs={24} data-test-id='contact-info'>
                    <SubTitle>
                      <FormattedMessage id='common.titles.CONTACT_INFO' />
                    </SubTitle>
                    <ContactInfo
                      data={data}
                      isEditable={isEditable}
                      form={form}
                      disabled={disabled}
                    />
                  </Col>
                </Row>
                <Row type='flex' gutter={16}>
                  {/* Check for length >  and not just for length,
                  otherwise a 0 will be displayed */}
                  {data.contactPeople && data.contactPeople.length > 0 &&
                    <Col sm={24} xs={24} data-test-id='contacts'>
                      <SubTitle>
                        <FormattedMessage id='common.titles.CONTACTS' />
                      </SubTitle>
                      <Contacts
                        isEditable={isEditable}
                        disabled={disabled}
                        contacts={data.contactPeople}
                        form={form}
                      />
                    </Col>
                  }
                </Row>
              </Form>
            </Info>
          </>
        }
        {isEditable &&
          <Prompt when={dirty} message={intl.formatMessage({ id: 'prompt.UNSAVED_CHANGES' })} />
        }
      </>
    );
  }
}

export default compose(
  Form.create(),
  withRouter,
  injectIntl,
)(InstalledAtInfo);
